/**
 * IndexedDB-based file storage utility for persisting File objects
 * across browser sessions.
 */

const DB_NAME = 'TravelExpenseFiles';
const DB_VERSION = 1;
const STORE_NAME = 'files';

export interface StoredFile {
  id: string;
  file: File;
  metadata: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  };
  createdAt: number;
}

class FileStorageManager {
  private static instance: FileStorageManager;
  private db: IDBDatabase | null = null;

  private constructor() {}

  static getInstance(): FileStorageManager {
    if (!FileStorageManager.instance) {
      FileStorageManager.instance = new FileStorageManager();
    }
    return FileStorageManager.instance;
  }

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  /**
   * Store a file in IndexedDB and return its unique ID
   */
  async storeFile(file: File): Promise<string> {
    const db = await this.ensureDB();
    const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const storedFile: StoredFile = {
      id: fileId,
      file: file,
      metadata: {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      },
      createdAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(storedFile);

      request.onsuccess = () => {
        console.log(`File stored with ID: ${fileId}`);
        resolve(fileId);
      };

      request.onerror = () => {
        console.error('Failed to store file:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Retrieve a file from IndexedDB by its ID
   */
  async getFile(fileId: string): Promise<File | null> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(fileId);

      request.onsuccess = () => {
        const result = request.result as StoredFile | undefined;
        if (result) {
          resolve(result.file);
        } else {
          console.warn(`File not found with ID: ${fileId}`);
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('Failed to retrieve file:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a file from IndexedDB
   */
  async deleteFile(fileId: string): Promise<boolean> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(fileId);

      request.onsuccess = () => {
        console.log(`File deleted with ID: ${fileId}`);
        resolve(true);
      };

      request.onerror = () => {
        console.error('Failed to delete file:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all stored file IDs
   */
  async getAllFileIds(): Promise<string[]> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result as string[]);
      };

      request.onerror = () => {
        console.error('Failed to get file IDs:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clean up orphaned files that are no longer referenced
   */
  async cleanupOrphanedFiles(referencedFileIds: string[]): Promise<void> {
    const allFileIds = await this.getAllFileIds();
    const orphanedIds = allFileIds.filter(id => !referencedFileIds.includes(id));

    console.log(`Cleaning up ${orphanedIds.length} orphaned files`);
    
    for (const fileId of orphanedIds) {
      try {
        await this.deleteFile(fileId);
      } catch (error) {
        console.error(`Failed to delete orphaned file ${fileId}:`, error);
      }
    }
  }

  /**
   * Clear all stored files
   */
  async clearAllFiles(): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('All files cleared from storage');
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to clear files:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
export const fileStorage = FileStorageManager.getInstance();
